/**
 * Helpers puros compartidos entre summaryReportTemplate.js (HTML/print,
 * legacy) y summaryReportPdfDocument.js (pdfmake). Nada de HTML ni de
 * pdfmake acá: solo formateo de datos y las decisiones de negocio sobre
 * qué mostrar (ninguna de estas es lógica de paginación física).
 */

const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// A partir de esta cantidad de agentes, la lista sale de la columna de la
// ficha de alcance y pasa a mostrarse aparte, a todo el ancho.
export const AGENTS_INLINE_MAX = 4;

// Aun a todo el ancho, listar cientos de nombres desborda la página 1 (no
// hay una segunda hoja con membrete a la que seguir). A partir de esta
// cantidad el resto se resume como texto ("y N más").
export const AGENTS_WIDE_DISPLAY_MAX = 30;

export function pad2(n) {
  return String(n).padStart(2, "0");
}

export function parseDateOnly(value) {
  if (!value) return null;
  const datePart = String(value).split(" ")[0].split("T")[0];
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function formatDateLong(d) {
  return `${d.getDate()} de ${MONTHS_ES[d.getMonth()]} de ${d.getFullYear()}`;
}

export function formatDateShort(d) {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export function formatDateRangeLong(from, to, t) {
  const dFrom = parseDateOnly(from);
  const dTo = parseDateOnly(to);
  if (!dFrom && !dTo) {
    return t("insights.report.noPeriod", { defaultValue: "Período no especificado" });
  }
  if (dFrom && dTo) {
    const sameMonth = dFrom.getMonth() === dTo.getMonth() && dFrom.getFullYear() === dTo.getFullYear();
    if (sameMonth) {
      return `${dFrom.getDate()} al ${dTo.getDate()} de ${MONTHS_ES[dTo.getMonth()]} de ${dTo.getFullYear()}`;
    }
    return `${formatDateLong(dFrom)} al ${formatDateLong(dTo)}`;
  }
  return formatDateLong(dFrom || dTo);
}

export function formatDateRangeShort(from, to, t) {
  const dFrom = parseDateOnly(from);
  const dTo = parseDateOnly(to);
  if (!dFrom && !dTo) {
    return t("insights.report.noPeriod", { defaultValue: "Período no especificado" });
  }
  if (dFrom && dTo) return `${formatDateShort(dFrom)} – ${formatDateShort(dTo)}`;
  return formatDateShort(dFrom || dTo);
}

export function formatGeneratedAt(value) {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getDate()} de ${MONTHS_ES[d.getMonth()]} de ${d.getFullYear()}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

export function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [String(value)];
}

export function formatListField(value, t) {
  const all = t("insights.all", { defaultValue: "Todos" });
  const list = toArray(value);
  if (list.length === 0) return all;
  return list.join(", ");
}

export function sanitizeFilenamePart(value) {
  return String(value).replace(/[\\/:*?"<>|]/g, "-");
}

/**
 * Decide cómo mostrar la lista de agentes en la ficha de alcance: inline
 * (columna normal, 1/3 del ancho) si son pocos, o "wide" (línea propia a
 * todo el ancho, con resumen "y N más") a partir de AGENTS_INLINE_MAX.
 * Devuelve piezas crudas (no HTML ni texto ya unido): cada renderer decide
 * cómo unirlas (el legacy con "&nbsp;|&nbsp;" + escapeHtml, pdfmake con
 * texto plano).
 */
export function computeAgentsDisplay(agentValue, t) {
  const agentList = toArray(agentValue);
  const agentsOverflow = agentList.length > AGENTS_INLINE_MAX;
  const agentsLabel = agentsOverflow
    ? t("insights.report.facts.agentsTotal", { defaultValue: "Total de agentes" })
    : t("insights.report.facts.agents", { defaultValue: "Agentes" });
  const agentValueText = agentsOverflow
    ? String(agentList.length)
    : formatListField(agentValue, t);
  const agentsWideNames = agentList.slice(0, AGENTS_WIDE_DISPLAY_MAX);
  const agentsWideRemaining = agentList.length - agentsWideNames.length;

  return {
    agentList,
    agentsOverflow,
    agentsLabel,
    agentValueText,
    agentsWideNames,
    agentsWideRemaining,
  };
}

/**
 * Nombre de archivo del PDF descargado (sin extensión), compartido entre
 * el flujo legacy (usado hoy como <title> del documento HTML) y pdfmake
 * (usado como nombre real del archivo descargado).
 */
export function buildSummaryReportFilename(detail, t) {
  const translate = t || ((key, opts) => (opts && opts.defaultValue) || key);
  const name = (detail?.name || "").trim()
    || translate("insights.report.unnamed", { defaultValue: "Resumen sin nombre" });
  const periodShort = formatDateRangeShort(detail?.dateRange?.from, detail?.dateRange?.to, translate);
  return `${translate("insights.report.pdfTitlePrefix", { defaultValue: "Informe CX" })} - ${sanitizeFilenamePart(name)} - ${sanitizeFilenamePart(periodShort)}`;
}

/**
 * feature 025 (US6): texto de cobertura del documento. Con `coverage` (corridas de la 025) declara
 * «N de M llamadas analizadas · K sin análisis de experiencia», más una nota si la cobertura fue
 * parcial y otra si hubo llamadas con análisis en forma no reconocida — las mismas frases que el
 * detalle en pantalla. Sin `coverage` (registros anteriores) cae al texto de siempre con
 * `callsAnalyzed`. Sólo es texto: los denominadores del documento siguen siendo el número analizado.
 * @returns {{text: string, notes: string[]}}
 */
export function buildCoverageLine(detail, t) {
  const translate = t || ((key, opts) => (opts && opts.defaultValue) || key);
  const coverage = detail?.coverage;

  if (!coverage) {
    const callsAnalyzed = Number(detail?.callsAnalyzed) || 0;
    return {
      text: `${callsAnalyzed} ${translate("insights.report.interactionsAnalyzed", { defaultValue: "interacciones analizadas" })}`,
      notes: [],
    };
  }

  // Opción A (revisión del PR, T086): nunca se excluyen llamadas; excluded/partial se ignoran
  const { found = 0, analyzed = 0, withoutCx = 0, degraded = 0 } = coverage;
  const text = translate("insights.detail.coverageLine", {
    analyzed,
    found,
    withoutCx,
    defaultValue: `${analyzed} de ${found} llamadas analizadas · ${withoutCx} sin análisis de experiencia`,
  });

  const notes = [];
  if (degraded > 0) {
    notes.push(translate("insights.detail.coverageDegraded", {
      degraded,
      defaultValue: `${degraded} con análisis en forma no reconocida`,
    }));
  }
  return { text, notes };
}
